<script lang="ts">
  import PageTitle from '#lib/ui/primitives/PageTitle.svelte';
  import { copyText } from '#lib/platform/clipboard.js';
  import { goto } from '$app/navigation';
  import { resolve } from '$app/paths';

  import { appName } from '#lib/deployment/brand.js';
  import { DEFAULT_APP_NAME } from '#lib/deployment/default-app-name.js';
  import { donateUrl, sourceUrl } from '#lib/deployment/links.js';
  import { useCoreClient } from '#lib/core/context.js';
  import { restartSetup } from '#lib/features/auth/setup/setup-record.js';
  import { writeText } from '#lib/platform/local-json.js';
  import { i18n } from '#lib/i18n.js';
  import { desktopUpdate, installPendingUpdate } from '#lib/platform/desktop-update.svelte.js';
  import {
    checkForMobileUpdate,
    checkForPackagedUpdate,
    checkForUpdate,
    relaunchApp,
    supportsAutoUpdate,
    updatePlatform,
  } from '#lib/platform/updates.js';
  import { desktopDataDir } from '#lib/platform/data-dir.js';
  import { electronBridge } from '#lib/platform/electron.js';
  import { hostsServiceWorker } from '#lib/platform/service-worker.js';
  import { checkForWebUpdate } from '#lib/platform/web-updates.svelte.js';
  import { preferences, setPreference } from '#lib/settings/preferences.svelte.js';
  import SableBrandMark from '#lib/ui/SableBrandMark.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import Tooltip from '#lib/ui/primitives/Tooltip.svelte';
  import ConfirmDialog from '#lib/ui/primitives/ConfirmDialog.svelte';
  import LinkButton from '#lib/ui/primitives/LinkButton.svelte';
  import SettingsRow from '#lib/ui/primitives/SettingsRow.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import Select from '#lib/ui/primitives/Select.svelte';
  import Switch from '#lib/ui/primitives/Switch.svelte';
  import '#lib/ui/primitives/settings-row.css';
  import CodeIcon from 'phosphor-svelte/lib/CodeIcon';
  import HeartIcon from 'phosphor-svelte/lib/HeartIcon';
  import CheckIcon from 'phosphor-svelte/lib/CheckIcon';
  import CopySimpleIcon from 'phosphor-svelte/lib/CopySimpleIcon';

  import HomeserverInfo from './HomeserverInfo.svelte';
  import SupporterActivationSettings from './SupporterActivationSettings.svelte';
  import { supporter } from '#lib/supporter/supporter.svelte.js';

  const core = useCoreClient();
  const source = sourceUrl();
  const donate = donateUrl();
  const version = `v${import.meta.env.VITE_APP_VERSION ?? 'dev'}`;
  const automaticUpdateChecksSupported = supportsAutoUpdate() || hostsServiceWorker();
  let confirmingReset = $state(false);
  let resetting = $state(false);
  let resetFailed = $state(false);
  let restarting = $state(false);
  let restartFailed = $state(false);
  let checkingForUpdate = $state(false);
  let updateCheckResult = $state<
    'available' | 'current' | 'mobile-available' | 'package-available' | 'failed' | null
  >(null);

  const pendingUpdate = $derived(supportsAutoUpdate() ? desktopUpdate.update : null);
  const pendingStage = $derived(desktopUpdate.stage);

  let versionCopied = $state(false);
  const dataDir = desktopDataDir().catch(() => null);
  async function copyVersion(): Promise<void> {
    await copyText(version);
    versionCopied = true;
    setTimeout(() => {
      versionCopied = false;
    }, 2000);
  }

  async function resetCaches(): Promise<void> {
    resetting = true;
    resetFailed = false;
    try {
      await core.resetCaches();
      await electronBridge()?.clearWebCaches();
      window.location.reload();
    } catch {
      resetting = false;
      resetFailed = true;
    }
  }

  async function runSetupAgain(): Promise<void> {
    const session = core.session;
    if (!session) return;
    restarting = true;
    restartFailed = false;
    try {
      await restartSetup(core, { setItem: writeText }, session.user_id, session.device_id);
      await goto(resolve('setup'));
    } catch {
      restartFailed = true;
    } finally {
      restarting = false;
    }
  }

  async function checkForUpdates(): Promise<void> {
    if (checkingForUpdate) return;
    checkingForUpdate = true;
    updateCheckResult = null;
    try {
      const platform = updatePlatform();
      if (platform === 'desktop' && !supportsAutoUpdate()) {
        updateCheckResult = (await checkForPackagedUpdate()) ? 'package-available' : 'current';
        return;
      }
      const available =
        platform === 'desktop'
          ? Boolean(await checkForUpdate())
          : platform === 'web'
            ? await checkForWebUpdate()
            : await checkForMobileUpdate();
      updateCheckResult = available
        ? platform === 'mobile'
          ? 'mobile-available'
          : 'available'
        : 'current';
    } catch {
      updateCheckResult = 'failed';
    } finally {
      checkingForUpdate = false;
    }
  }
</script>

<PageTitle title={i18n.t('settings.about')} />

<div class="about-page">
  <header class="product">
    <SableBrandMark class="about-logo" />
    <div>
      <div class="product-name">
        <h1>{appName()}</h1>
        <Tooltip label={i18n.t(versionCopied ? 'common.copied' : 'common.copy')}>
          {#snippet trigger({ props })}
            <button
              {...props}
              class="version-copy-button"
              type="button"
              onclick={() => void copyVersion()}
            >
              {version}
              {#if versionCopied}
                <CheckIcon size="1em" aria-hidden="true" />
              {:else}
                <CopySimpleIcon size="1em" aria-hidden="true" />
              {/if}
            </button>
          {/snippet}
        </Tooltip>
      </div>
      {#if appName() !== DEFAULT_APP_NAME}
        <p>{i18n.t('settings.aboutPoweredBy')}</p>
      {/if}
      <p>{i18n.t('settings.aboutTagline')} 🇵🇸 🇺🇦 🏳️‍⚧️</p>
      {#if source || donate}
        <div class="product-actions">
          {#if source}
            <LinkButton href={source} target="_blank" rel="noopener noreferrer" size="small">
              <CodeIcon aria-hidden="true" />
              {i18n.t('common.sourceCode')}
            </LinkButton>
          {/if}
          {#if donate}
            <LinkButton
              href={donate}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              size="small"
            >
              <HeartIcon aria-hidden="true" />
              {i18n.t('common.supportSable')}
            </LinkButton>
          {/if}
        </div>
      {/if}
    </div>
  </header>
  {#if supporter.enabled && donate}
    <SupporterActivationSettings />
  {/if}
  <svelte:boundary>
    <HomeserverInfo />

    {#snippet pending()}{/snippet}
  </svelte:boundary>

  <SettingsSection title={i18n.t('settings.aboutOptions')} headingId="about-options">
    <ul class="settings">
      <SettingsRow id="report-issue" title={i18n.t('settings.aboutReportIssue')}>
        <LinkButton href={resolve('bugreport')} size="small">
          {i18n.t('settings.aboutReport')}
        </LinkButton>
      </SettingsRow>
      {#await dataDir then dir}
        {#if dir}
          <SettingsRow
            id="data-folder"
            title={i18n.t('settings.aboutDataFolder')}
            description={dir}
          >
            <Button size="small" onclick={() => void copyText(dir)}>
              {i18n.t('common.copy')}
            </Button>
          </SettingsRow>
        {/if}
      {/await}
      {#if automaticUpdateChecksSupported}
        <SettingsRow
          id="auto-update-check"
          key="autoUpdateCheck"
          title={i18n.t('settings.autoUpdateCheck')}
          description={i18n.t('settings.autoUpdateCheckHint')}
          control="auto-update-check-switch"
        >
          <Switch
            id="auto-update-check-switch"
            checked={preferences.autoUpdateCheck}
            label={i18n.t('settings.autoUpdateCheck')}
            onCheckedChange={(checked) => setPreference('autoUpdateCheck', checked)}
          />
        </SettingsRow>
      {/if}
      {#if supportsAutoUpdate()}
        <SettingsRow
          id="update-channel"
          key="updateChannel"
          title={i18n.t('settings.updateChannel')}
          description={i18n.t('settings.updateChannelHint')}
        >
          <Select
            bind:value={
              () => preferences.updateChannel, (value) => setPreference('updateChannel', value)
            }
            aria-label={i18n.t('settings.updateChannel')}
            items={[
              { value: 'stable', label: i18n.t('settings.updateChannelStable') },
              { value: 'nightly', label: i18n.t('settings.updateChannelNightly') },
            ]}
          />
        </SettingsRow>
      {/if}
      <SettingsRow id="check-for-updates" title={i18n.t('settings.aboutCheckForUpdates')}>
        <Button size="small" loading={checkingForUpdate} onclick={() => void checkForUpdates()}>
          {i18n.t('settings.aboutCheckForUpdates')}
        </Button>
      </SettingsRow>
      {#if pendingUpdate}
        {@const version = pendingUpdate.version}
        <SettingsRow
          id="pending-update"
          title={pendingStage.name === 'staged'
            ? i18n.t('settings.updateBannerStagedTitle')
            : pendingStage.name === 'failed'
              ? i18n.t('settings.updateBannerFailedTitle')
              : i18n.t('settings.updateBannerTitle')}
          description={pendingStage.name === 'staged'
            ? i18n.t('settings.updateBannerStagedBody', { version })
            : pendingStage.name === 'failed'
              ? pendingStage.message
              : pendingStage.name === 'downloading'
                ? i18n.t('settings.updateBannerProgress', { percent: pendingStage.percent })
                : i18n.t('settings.updateBannerBody', { version })}
        >
          {#if pendingStage.name === 'staged'}
            <Button variant="primary" size="small" onclick={() => void relaunchApp()}>
              {i18n.t('settings.updateBannerRestart')}
            </Button>
          {:else}
            <Button
              variant="primary"
              size="small"
              loading={pendingStage.name === 'downloading'}
              onclick={() => void installPendingUpdate()}
            >
              {pendingStage.name === 'failed' ? i18n.t('common.retry') : i18n.t('common.install')}
            </Button>
          {/if}
        </SettingsRow>
      {/if}
      {#if updateCheckResult === 'current'}
        <li class="settings-form status" aria-live="polite">
          {i18n.t('settings.aboutUpToDate')}
        </li>
      {:else if updateCheckResult === 'available' && !pendingUpdate}
        <li class="settings-form status" aria-live="polite">
          {i18n.t('settings.aboutUpdateAvailable')}
        </li>
      {:else if updateCheckResult === 'mobile-available'}
        <li class="settings-form status" aria-live="polite">
          {i18n.t('settings.aboutMobileUpdateAvailable')}
        </li>
      {:else if updateCheckResult === 'package-available'}
        <li class="settings-form status" aria-live="polite">
          {i18n.t('settings.aboutPackageUpdateAvailable')}
        </li>
      {:else if updateCheckResult === 'failed'}
        <li class="settings-form error" role="alert">
          {i18n.t('settings.aboutUpdateCheckFailed')}
        </li>
      {/if}
      <SettingsRow
        id="run-setup-again"
        title={i18n.t('settings.aboutRunSetup')}
        description={i18n.t('settings.aboutRunSetupHint')}
      >
        <Button size="small" loading={restarting} onclick={() => void runSetupAgain()}>
          {i18n.t('settings.aboutRunSetupAction')}
        </Button>
      </SettingsRow>
      {#if restartFailed}
        <li class="settings-form error" role="alert">{i18n.t('settings.actionFailed')}</li>
      {/if}
      <SettingsRow
        id="reset-cache"
        title={i18n.t('settings.aboutResetCache')}
        description={i18n.t('settings.aboutResetCacheHint')}
      >
        <Button
          size="small"
          loading={resetting}
          onclick={() => {
            confirmingReset = true;
          }}
        >
          {i18n.t('common.reset')}
        </Button>
      </SettingsRow>
      {#if resetFailed}
        <li class="settings-form error" role="alert">{i18n.t('settings.aboutResetFailed')}</li>
      {/if}
    </ul>
  </SettingsSection>
</div>

<ConfirmDialog
  bind:open={confirmingReset}
  title={i18n.t('settings.aboutResetCacheConfirmTitle')}
  description={i18n.t('settings.aboutResetCacheConfirmDescription')}
  confirmLabel={i18n.t('common.reset')}
  busy={resetting}
  onConfirm={() => void resetCaches()}
/>

<style>
  .about-page {
    display: grid;
    gap: var(--space-500);
    margin: 0 auto;
    max-width: 52rem;
    min-width: 0;
    padding: var(--page-gutter);
  }

  .product {
    align-items: flex-start;
    display: flex;
    gap: var(--space-400);
  }

  :global(.about-logo) {
    flex: 0 0 auto;
    height: 3.75rem;
    width: 3.75rem;
  }

  .product > div {
    min-width: 0;
  }

  .product-name,
  .product-actions {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-300);
  }

  h1 {
    font-size: var(--font-size-heading);
    line-height: var(--line-height-heading);
    margin: 0;
  }

  .version-copy-button {
    align-items: end;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    font-size: var(--font-size-subheading);
    gap: var(--space-150);
    height: var(--line-height-heading);
    line-height: var(--line-height-heading);
    margin: 0;
    padding: 0;
    user-select: text;
  }

  .version-copy-button,
  p {
    color: var(--surface-var-on-container);
  }

  p {
    margin: var(--space-200) 0 var(--space-300);
  }

  .settings {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .error {
    color: var(--crit-main);
    margin: 0;
  }

  .status {
    color: var(--surface-var-on-container);
    margin: 0;
  }

  @media (width < 42rem) {
    .product {
      flex-direction: column;
    }

    .product-actions {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
    }

    .product-actions :global(.btn) {
      min-width: 0;
      width: 100%;
    }
  }
</style>
